// ListAgeLimitePaiementRente.jsx
// Ant Design Table + bouton "Ajouter" à gauche, pas de barre de recherche.
// Actions via menu "..." (Dropdown). Appels API intégrés.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Table, Modal, Dropdown } from "antd";
import { PlusOutlined, MoreOutlined, ExclamationCircleFilled, DownloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import SideBar from "../../../components/SideBar";     // ajuste les chemins si besoin
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import AddAgeLimitePaiementRente from "./AddAgeLimitePaiementRente";
import { API_URL, TOKEN } from "../../../variables/constants"; // ajuste le chemin si besoin
import * as XLSX from "xlsx";

const { confirm } = Modal;

const ListAgeLimitePaiementRente = () => {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const headers = {
    Authorization: `Bearer ${TOKEN}`,
    "Content-Type": "application/json",
  };

  const fetchAll = () => {
    axios
      .get(`${API_URL}/api/age-limite-paiement-rente`, { headers })
      .then((res) => {
        const data = [...res.data].sort(
          (a, b) => (b.ageLimitePaiementRenteId || 0) - (a.ageLimitePaiementRenteId || 0)
        );
        setRows(data);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des données");
      });
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleShow = (edit = false, item = null) => {
    setEditMode(edit);
    setSelectedItem(item);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setEditMode(false);
    setSelectedItem(null);
    // Option fiable : re-fetch à la fermeture (comme tes pages Antd)
    fetchAll();
  };

  const onAdd = (newRow) => setRows((prev) => [newRow, ...prev]);
  const onUpdate = (up) =>
    setRows((prev) =>
      prev.map((r) => (r.ageLimitePaiementRenteId === up.ageLimitePaiementRenteId ? up : r))
    );

  const askDelete = (id) => {
    confirm({
      title: "Supprimer cette ligne ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => onDelete(id),
    });
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/age-limite-paiement-rente/delete/${id}`, { headers });
      setRows((prev) => prev.filter((r) => r.ageLimitePaiementRenteId !== id));
      toast.success("Supprimé avec succès");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExportExcel = () => {
    if (!rows.length) {
      toast.warning("Aucune donnée à exporter");
      return;
    }

    const worksheetData = rows.map((row) => ({
      "Âge limite": row.ageLimite ?? "",
      "Âge": row.age ?? "",
      Sexe: row.sexe ?? "",
      "Prix 1 franc rente": row.prixUnFrancRente ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AgeLimiteRente");
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `age_limite_rente_${today}.xlsx`);
  };

  // Colonnes Antd (style screenshot)
  const columns = useMemo(
    () => [
      {
        title: "Âge limite",
        dataIndex: "ageLimite",
        key: "ageLimite",
        sorter: (a, b) => (a.ageLimite ?? 0) - (b.ageLimite ?? 0),
        width: 140,
      },
      {
        title: "Âge",
        dataIndex: "age",
        key: "age",
        sorter: (a, b) => (a.age ?? 0) - (b.age ?? 0),
        width: 120,
      },
      {
        title: "Sexe",
        dataIndex: "sexe",
        key: "sexe",
        sorter: (a, b) => String(a.sexe || "").localeCompare(String(b.sexe || "")),
        width: 120,
      },
      {
        title: "Prix 1 franc rente",
        dataIndex: "prixUnFrancRente",
        key: "prixUnFrancRente",
        render: (v) =>
          v == null ? "" : Number(v).toLocaleString("fr-FR", { maximumFractionDigits: 4 }),
        sorter: (a, b) => (a.prixUnFrancRente ?? 0) - (b.prixUnFrancRente ?? 0),
        width: 200,
      },
      {
        title: "Actions",
        key: "actions",
        align: "right",
        width: 80,
        render: (_, record) => {
          const menuItems = [
            { key: "edit", label: "Éditer", onClick: () => handleShow(true, record) },
            { key: "delete", label: "Supprimer", danger: true, onClick: () => askDelete(record.ageLimitePaiementRenteId) },
          ];
          return (
            <Dropdown
              menu={{
                items: menuItems.map((m) => ({
                  key: m.key,
                  danger: m.danger,
                  label: <a onClick={(e) => { e.preventDefault(); m.onClick(); }}>{m.label}</a>,
                })),
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <a onClick={(e) => e.preventDefault()} className="text-body">
                <MoreOutlined />
              </a>
            </Dropdown>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* Boutons actions */}
              <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleShow(false, null)}>
                  Ajouter
                </Button>
                <Button icon={<DownloadOutlined />} onClick={handleExportExcel} disabled={!rows.length}>
                  Exporter
                </Button>
              </div>

              <div className="card">
                <div className="table-responsive text-nowrap">
                  <Table
                    rowKey="ageLimitePaiementRenteId"
                    columns={columns}
                    dataSource={rows}
                    pagination={{
                      current: currentPage,
                      pageSize,
                      total: rows.length,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20","30","40","50","60","70","80","90","100"],
                      onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      },
                      onShowSizeChange: (_, size) => {
                        setCurrentPage(1);
                        setPageSize(size);
                      },
                    }}
                  />
                </div>
              </div>

              <AddAgeLimitePaiementRente
                show={show}
                handleClose={handleClose}
                editMode={editMode}
                selectedItem={selectedItem}
                items={rows}
                onAdd={onAdd}
                onUpdate={onUpdate}
              />
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListAgeLimitePaiementRente;
